import { useEffect, useRef, useState } from 'react';
import { Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useApiClient } from '@/shared/api';

type Recording = { index: number; text: string; url: string };
type VoiceSettings = {
  stability: number;
  similarity_boost: number;
  style: number;
  use_speaker_boost: boolean;
  speed: number;
};
type PreviewSet = {
  batch_id: string;
  source_text: string;
  voice_settings: VoiceSettings;
  model: string;
  created_at: string;
  recordings: Recording[];
};

type VoicePreviewProps = {
  voiceId: number;
  provider: string;
};

const PLACEHOLDER =
  'Thank you for your call, how can I help you today?\nWe appreciate having you as a customer, have a great day!';

function formatParams(settings: VoiceSettings) {
  const parts = [
    `speed ${settings.speed}`,
    `stability ${settings.stability}`,
    `similarity ${settings.similarity_boost}`,
  ];
  if (settings.style > 0) parts.push(`style ${settings.style}`);
  if (settings.use_speaker_boost) parts.push('boost');
  return parts.join(' · ');
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function VoicePreview({ voiceId, provider }: VoicePreviewProps) {
  const [text, setText] = useState('');
  const [expandedSets, setExpandedSets] = useState<Set<string>>(new Set());
  const initializedRef = useRef(false);
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const isElevenLabs = provider === 'elevenlabs';
  const queryKey = ['tts-previews', voiceId];

  const { data: previewSets = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => apiClient<PreviewSet[]>(`/tts/previews/${voiceId}`),
    staleTime: 1000 * 60 * 5,
    enabled: isElevenLabs,
  });

  useEffect(() => {
    initializedRef.current = false;
    setText('');
    setExpandedSets(new Set());
  }, [voiceId]);

  useEffect(() => {
    if (previewSets.length > 0 && !initializedRef.current) {
      setText(previewSets[0].source_text);
      setExpandedSets(new Set([previewSets[0].batch_id]));
      initializedRef.current = true;
    }
  }, [previewSets]);

  const generateMutation = useMutation({
    mutationFn: (params: { voice_id: number; text: string }) =>
      apiClient<PreviewSet>('/tts/generate-preview', {
        method: 'POST',
        body: JSON.stringify(params),
      }),
    onSuccess: (newSet) => {
      queryClient.setQueryData(queryKey, (old: PreviewSet[] = []) => [newSet, ...old]);
      setExpandedSets((prev) => new Set([...prev, newSet.batch_id]));
      initializedRef.current = true;
    },
  });

  if (!isElevenLabs) return null;

  const handleGenerate = () => {
    generateMutation.mutate({ voice_id: voiceId, text });
  };

  const toggleExpanded = (batchId: string) => {
    setExpandedSets((prev) => {
      const next = new Set(prev);
      if (next.has(batchId)) next.delete(batchId);
      else next.add(batchId);
      return next;
    });
  };

  const nonEmptyLines = text.split('\n').filter((l) => l.trim().length > 0).length;

  return (
    <Card className="border-gray-300">
      <CardHeader className="bg-gray-50 border-b border-gray-300 py-2">
        <CardTitle className="text-body font-semibold">Voice Preview</CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-3">
        <p className="text-detail text-muted-foreground">
          Each line will be generated as a separate recording
        </p>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={PLACEHOLDER}
          className="resize-none"
          rows={4}
          maxLength={2000}
        />
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleGenerate}
            disabled={generateMutation.isPending || nonEmptyLines === 0}
          >
            {generateMutation.isPending && <Loader2 className="w-3 h-3 mr-2 animate-spin" />}
            {generateMutation.isPending ? 'Generating...' : 'Generate Preview'}
          </Button>
          <span className="text-detail text-muted-foreground">
            {nonEmptyLines} line{nonEmptyLines !== 1 ? 's' : ''} · {text.length}/2000
          </span>
        </div>

        {generateMutation.isError && (
          <p className="text-detail text-destructive">
            {generateMutation.error?.message || 'Generation failed'}
          </p>
        )}

        {isLoading && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span className="text-detail">Loading previews...</span>
          </div>
        )}

        {previewSets.length > 0 && (
          <div className="space-y-2 pt-1">
            {previewSets.map((set) => {
              const isExpanded = expandedSets.has(set.batch_id);
              return (
                <div key={set.batch_id} className="border rounded-md">
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                    onClick={() => toggleExpanded(set.batch_id)}
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-3 h-3 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-3 h-3 shrink-0 text-muted-foreground" />
                    )}
                    <span className="text-detail font-medium truncate">
                      {formatDate(set.created_at)}
                    </span>
                    <span className="text-detail text-muted-foreground truncate">
                      {formatParams(set.voice_settings)}
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="px-3 pb-3 space-y-2">
                      {set.recordings.map((rec) => (
                        <div key={rec.index} className="space-y-1">
                          <p className="text-detail text-muted-foreground">{rec.text}</p>
                          <audio controls src={rec.url} className="w-full" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
